import { z } from "zod"
import { Errors } from "../errors"
import { ChannelEnum } from "../campaigns/commonTypes"
import { ConversationFlowEnum } from "../conversations/response_data"
import { createFilterBySchema } from "../shared/filter_dtos"
import { requiredStringOrDateToDate } from "../shared/utils"
import { ClusterDomainSchema } from "./response_data"
import { TimeDimensionSchema, TimeFrameEnum, TimeFrameSchema } from "./timeFrame"

export const DEFAULT_ACTIVITY_TIME_FRAME = TimeFrameEnum.LastMonth
export const InsightsAlgorithmSchema = z.enum(["kmeans", "hdbscan"])
export type InsightsAlgorithm = z.infer<typeof InsightsAlgorithmSchema>

export const CanonicalSchema = z
  .union([z.literal("true"), z.literal("false"), z.boolean()])
  .optional()
  .transform((val) => {
    if (typeof val === "boolean") return val
    if (val === "true") return true
    if (val === "false") return false
    return undefined
  })

export const GroupNonCategoryTopicsSchema = z
  .union([z.boolean(), z.string()])
  .transform((value) => (typeof value === "string" ? value === "true" : value))
  .optional()

const SegmentIdsSchema = z
  .array(z.string().regex(/^\d+$/))
  .optional()
  .transform((value) => value?.map(BigInt))

export const GetMetricSchema = z.object({
  timeFrame: TimeFrameSchema.default(TimeFrameEnum.LastMonth),
  campaignId: z
    .string()
    .optional()
    .transform((value) => (value ? BigInt(value) : undefined)),
  segmentIds: SegmentIdsSchema,
  channel: z.enum(ChannelEnum).optional(),
  conversationFlow: z.enum(ConversationFlowEnum).optional(),
})

export const GetActivitySchema = z.object({
  campaignId: z
    .string()
    .optional()
    .transform((value) => (value ? BigInt(value) : undefined)),
  segmentIds: SegmentIdsSchema,
  channel: z.enum(ChannelEnum).optional(),
  conversationFlow: z.enum(ConversationFlowEnum).optional(),
  timeFrame: TimeFrameSchema.default(DEFAULT_ACTIVITY_TIME_FRAME),
  timeDimension: TimeDimensionSchema.optional(),
})

export const GetInsightSchema = GetMetricSchema.extend({
  campaignId: z
    .string()
    .optional()
    .transform((value) => (value ? BigInt(value) : undefined)),
  goalId: z
    .string()
    .optional()
    .transform((value) => (value ? BigInt(value) : undefined)),
})

export const GetGoalInsightSchema = GetInsightSchema.extend({
  goalId: z.string().transform(BigInt),
})

export const GetInsightsActivitySchema = z.object({
  campaignId: z
    .string()
    .optional()
    .transform((value) => (value ? BigInt(value) : undefined)),
})

export const GetCampaignActivitySchema = z.object({
  campaignId: z.string().transform(BigInt),
  weekly: z.boolean().optional().default(false),
})

export const GetSentimentActivitySchema = z.object({
  campaignId: z
    .string()
    .optional()
    .transform((value) => (value ? BigInt(value) : undefined)),
  goalId: z
    .string()
    .optional()
    .transform((value) => (value ? BigInt(value) : undefined)),
})

export const GetCustomerSentimentSchema = z.object({
  customerId: z.string().transform(BigInt),
})

export const GetResponsesActivitySchema = z.object({
  goalId: z.string().transform(BigInt),
  campaignId: z
    .string()
    .optional()
    .transform((value) => (value ? BigInt(value) : undefined)),
})

export const GetCanonicalTopicSchema = z.object({
  domain: ClusterDomainSchema,
  segmentId: z.string().optional(),
  goalId: z.string().optional(),
  timeFrame: TimeFrameSchema.optional().default(TimeFrameEnum.Last3Months),
  withSnapshots: z
    .union([z.boolean(), z.string()])
    .transform((value) => {
      if (typeof value === "string") return value === "true"
      return value
    })
    .optional()
    .default(false),
})

export const GetCanonicalTopicsTimelineSchema = GetCanonicalTopicSchema.extend({
  groupNonCategoryTopics: GroupNonCategoryTopicsSchema,
})

export const CanonicalTopicGroupedByEnum = {
  company: "company",
  campaign: "campaign",
  segmentGroup: "segmentGroup",
} as const

export const CanonicalTopicGroupedBy = z.union([
  z.object({
    type: z.literal(CanonicalTopicGroupedByEnum.company),
  }),
  z.object({
    type: z.literal(CanonicalTopicGroupedByEnum.campaign),
  }),
  z.object({
    type: z.literal(CanonicalTopicGroupedByEnum.segmentGroup),
    segmentGroupId: z.string(),
  }),
])

export type CanonicalTopicGroupedBy = z.infer<typeof CanonicalTopicGroupedBy>

export const CanonicalTopicFilterByEnum = {
  segments: "segments",
  campaignId: "campaignId",
  channel: "channel",
  conversationFlow: "conversationFlow",
} as const

export const CanonicalTopicFilterBy = createFilterBySchema([
  {
    key: CanonicalTopicFilterByEnum.segments,
    value: z
      .array(
        z.object({
          groupId: z.string(),
          segmentId: z.string(),
        }),
      )
      .optional(),
  },
  {
    key: CanonicalTopicFilterByEnum.campaignId,
    value: z.string().optional(),
  },
  {
    key: CanonicalTopicFilterByEnum.channel,
    value: z.enum(ChannelEnum).optional(),
  },
  {
    key: CanonicalTopicFilterByEnum.conversationFlow,
    value: z.enum(ConversationFlowEnum).optional(),
  },
])

export type CanonicalTopicFilterBy = z.infer<typeof CanonicalTopicFilterBy>["filterBy"]

export const GetCanonicalTopicGroupedSchema = z
  .object({
    domain: ClusterDomainSchema,
    timeFrame: TimeFrameSchema.optional().default(TimeFrameEnum.Last3Months),
    groupedBy: CanonicalTopicGroupedBy,
    goalId: z.string().nullable().optional(),
    groupNonCategoryTopics: GroupNonCategoryTopicsSchema,
  })
  .extend(CanonicalTopicFilterBy.shape)

export const GetSignalsSchema = z.object({
  dateRange: z.object({
    from: requiredStringOrDateToDate(Errors.DATE_RANGE_INVALID),
    to: requiredStringOrDateToDate(Errors.DATE_RANGE_INVALID),
  }),
})

export const SolveSignalSchema = z.object({
  signalId: z.string().transform(BigInt),
})

export const SolveSignalBodySchema = z.object({
  signalIds: z.array(z.string().transform(BigInt)),
  isSolved: z.boolean().optional().default(true),
})

export const GetTopicActivitySchema = z
  .object({
    domain: ClusterDomainSchema,
    groupBy: z.enum(["day", "week"]).optional().default("day"),
  })
  .extend(CanonicalTopicFilterBy.shape)
